import { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Tutee } from '../../../types/tuition';
import AdminGroupSelector from './AdminGroupSelector';

interface StickyAdminGroupBarProps {
  tutees: Tutee[];
  selectedTuteeId: string;
  onSelect: (tuteeId: string) => void;
}

const StickyAdminGroupBar = ({ tutees, selectedTuteeId, onSelect }: StickyAdminGroupBarProps) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const [pinned, setPinned] = useState(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setPinned(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      },
      { threshold: 0, rootMargin: '0px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  const pinnedBar = pinned
    ? createPortal(
        <div className="fixed top-0 left-0 right-0 z-[200] pt-2 pb-2.5 border-b border-black/[0.06] bg-white/70 backdrop-blur-xl supports-[backdrop-filter]:bg-white/55 shadow-[0_1px_0_rgba(0,0,0,0.04)]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <AdminGroupSelector
              tutees={tutees}
              selectedTuteeId={selectedTuteeId}
              onSelect={onSelect}
              variant="sticky"
            />
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <>
      {pinnedBar}
      <div ref={sentinelRef} className="h-px w-full" aria-hidden />
      <div
        ref={barRef}
        className={`py-2 sm:py-3 px-1 sm:px-0 border-b border-gray-200/70 ${pinned ? 'invisible' : ''}`}
        aria-hidden={pinned}
      >
        <AdminGroupSelector
          tutees={tutees}
          selectedTuteeId={selectedTuteeId}
          onSelect={onSelect}
          variant="default"
        />
      </div>
    </>
  );
};

export default StickyAdminGroupBar;
